import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
} from "@mui/material";

import { getAllUsers, getAdminCompanies } from "../../../services/jobService";
import { paths } from "../../../routes/paths";
import { AdminPage } from "./AdminShell";
import AdminFilterBar, { companyOptions } from "./AdminFilterBar";
import useAdminFilters from "./useAdminFilters";
import tokens from "../../theme/tokens";

const { color, font } = tokens;

const LIMIT = 25;

const ROLE_LABEL = {
  super_admin: "Super Admin",
  company_admin: "Admin",
  member: "Member",
};

/** Cross-company user listing — every user on the platform, including deactivated
 *  ones and rows with no company. Super-admin only. */
const AllUsers = () => {
  const { filters, searchText, setSearch, setFilter, clearAll, page, setPage, hasFilters } =
    useAdminFilters(["q", "company_id", "role", "is_active"]);

  const [data, setData] = useState({ users: [], total_users: 0 });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAdminCompanies().then(setCompanies);
  }, []);

  useEffect(() => {
    let cancelled = false; // also the stale-response guard
    setLoading(true);
    setError(null);
    getAllUsers({ page, limit: LIMIT, ...filters })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Failed to load users.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.q, filters.company_id, filters.role, filters.is_active]);

  return (
    <AdminPage title="All Users" subtitle={`${data.total_users} across all companies.`}>
      <AdminFilterBar
        searchPlaceholder="Search name, email, company…"
        searchValue={searchText}
        onSearchChange={setSearch}
        hasFilters={hasFilters}
        onClear={clearAll}
        dropdowns={[
          {
            key: "company_id",
            label: "Company",
            value: filters.company_id,
            onChange: (value) => setFilter("company_id", value),
            options: companyOptions(companies, filters.company_id),
          },
          {
            key: "role",
            label: "Role",
            value: filters.role,
            onChange: (value) => setFilter("role", value),
            options: [
              { value: "", label: "All roles" },
              { value: "super_admin", label: "Super Admin" },
              { value: "company_admin", label: "Admin" },
              { value: "member", label: "Member" },
            ],
          },
          {
            key: "is_active",
            label: "Status",
            value: filters.is_active,
            onChange: (value) => setFilter("is_active", value),
            options: [
              { value: "", label: "All statuses" },
              { value: "true", label: "Active" },
              { value: "false", label: "Inactive" },
            ],
          },
        ]}
      />
      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress size={22} />
        </Box>
      ) : error ? (
        <Box sx={{ py: 5, textAlign: "center", font: `400 13px ${font.sans}`, color: color.danger }}>
          {error}
        </Box>
      ) : !data.users.length ? (
        <Box sx={{ py: 5, textAlign: "center", font: `400 13px ${font.sans}`, color: color.textSecondary }}>
          {hasFilters ? "No users match your filters." : "No users found."}
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: color.surface,
            border: `1px solid ${color.border}`,
            borderRadius: `${tokens.radius.card}px`,
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Jobs</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.name || "—"}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.company?.name || user.company?.email_domain || (
                      <Chip size="small" label="no company" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {/* A zero count is a dash, not a link — a link into an empty table is a
                        dead end that reads like a bug. */}
                    {user.jobs_count > 0 ? (
                      <Link
                        component={RouterLink}
                        to={`${paths.v1.allJobs}?owner_id=${user.id}`}
                        sx={{ font: `500 13px ${font.sans}` }}
                      >
                        {user.jobs_count}
                      </Link>
                    ) : (
                      <Box sx={{ font: `400 13px ${font.sans}`, color: color.textMuted }}>—</Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={ROLE_LABEL[user.role] || user.role} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={user.is_active ? "Active" : "Inactive"}
                      color={user.is_active ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={data.total_users}
            page={page - 1}
            onPageChange={(_e, p) => setPage(p + 1)}
            rowsPerPage={LIMIT}
            rowsPerPageOptions={[LIMIT]}
          />
        </Box>
      )}
    </AdminPage>
  );
};

export default AllUsers;
